import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { DateRangePicker } from '../../../../web/components-v2/DateRangePicker';
import { GoalsViewModel } from '../../../viewModels/goals';

interface IProps {
	defaultDate?: Date;
	goals: GoalsViewModel;
}

const ISO_FORMAT = 'YYYY-MM-DD';

const startOfDay = (value: Date | string): Date => moment(value, ISO_FORMAT).startOf('day').toDate();
// The API compares `Day <= endDate`, so the end must sit at the end of its day or the last day drops out.
const endOfDay = (value: Date | string): Date => moment(value, ISO_FORMAT).endOf('day').toDate();

export const GoalDatePicker: React.FC<IProps> = observer(({ defaultDate = new Date(), goals }) => {
	const [startDate, setStartDate] = useState(() => moment(defaultDate).startOf('day').toDate());
	const [endDate, setEndDate] = useState(() => moment(defaultDate).endOf('day').toDate());

	useEffect(() => {
		goals.reset();

		goals.startDate = startDate;

		goals.endDate = endDate;

		goals.load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startDate, endDate]);

	// The picker clears with empty strings; the goals page always needs a range, so a clear falls back to the default day.
	const onChange = (from: string, to: string) => {
		const nextStart = startOfDay(from || defaultDate);
		const nextEnd = endOfDay(to || defaultDate);
		if (nextStart.getTime() === startDate.getTime() && nextEnd.getTime() === endDate.getTime()) {
			return;
		}
		setStartDate(nextStart);
		setEndDate(nextEnd);
	};

	return (
		<DateRangePicker
			applyOnComplete={true}
			ariaLabel='Date Range'
			from={moment(startDate).format(ISO_FORMAT)}
			to={moment(endDate).format(ISO_FORMAT)}
			onChange={onChange}
		/>
	);
});
